import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send, Plus, Loader2, Sparkles, Trash2, MessageSquare, Pencil, Check, Paperclip, X, Download } from 'lucide-react';
import { api, fileUrl } from '@/lib/api';
import { luxe, gradientCss } from '@/lib/luxe';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const G = ['#EC4899', '#A855F7', '#8B5CF6'];
const SUGGEST = ['Ek SaaS landing page ka poora prompt likho', 'Meri EV scooter shop ke liye 7-din marketing plan do', 'Explain quantum computing simply', 'Startup idea for a tier-2 city — brainstorm karo'];

export default function MyraChat() {
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const scrollRef = useRef(null);

  const loadSessions = () => api('/myra-chat/sessions').then((r) => setSessions(r.items || [])).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);

  const openSession = async (sid) => {
    setSessionId(sid);
    const r = await api(`/myra-chat/session/${sid}`).catch(() => ({ messages: [] }));
    setMessages(r.messages || []);
  };
  const newChat = () => { setSessionId(null); setMessages([]); };

  const send = async (override) => {
    const q = (override ?? text).trim();
    if (!q || busy) return;
    setText('');
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const r = await api('/myra-chat/message', { method: 'POST', body: { session_id: sessionId, text: q } });
      setSessionId(r.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: r.reply }]);
      loadSessions();
    } catch (e) { toast.error(e.message); setMessages((m) => m.slice(0, -1)); } finally { setBusy(false); }
  };

  const delSession = async (sid, e) => { e.stopPropagation(); await api(`/myra-chat/session/${sid}`, { method: 'DELETE' }); if (sid === sessionId) newChat(); loadSessions(); };

  const startRename = (s, e) => { e.stopPropagation(); setEditingId(s.session_id); setEditTitle(s.title || ''); };
  const saveRename = async (sid, e) => {
    if (e) e.stopPropagation();
    const t = editTitle.trim();
    setEditingId(null);
    if (!t) return;
    setSessions((ss) => ss.map((x) => (x.session_id === sid ? { ...x, title: t } : x)));
    try { await api(`/myra-chat/session/${sid}/title`, { method: 'PATCH', body: { title: t } }); } catch (err) { toast.error(err.message); loadSessions(); }
  };

  return (
    <div className="flex gap-4" style={{ height: 'calc(100vh - 130px)' }}>
      {/* sessions */}
      <div className="hidden md:flex flex-col w-60 shrink-0 p-3 rounded-2xl" style={{ background: luxe.bgElev, border: `1px solid ${luxe.border}` }}>
        <button onClick={newChat} data-testid="myra-new-chat" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold luxe-btn mb-3" style={{ background: gradientCss(G), color: '#fff' }}><Plus size={16} /> New Chat</button>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.map((s) => (
            <div key={s.session_id} onClick={() => editingId !== s.session_id && openSession(s.session_id)} className="group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer text-sm" style={{ background: s.session_id === sessionId ? luxe.surface2 : 'transparent', color: s.session_id === sessionId ? luxe.text : luxe.textMid }}>
              {editingId === s.session_id ? (
                <input
                  autoFocus
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveRename(s.session_id); if (e.key === 'Escape') setEditingId(null); }}
                  onBlur={() => saveRename(s.session_id)}
                  data-testid="myra-rename-input"
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm rounded px-1"
                  style={{ color: luxe.text, border: `1px solid ${luxe.border}` }}
                />
              ) : (
                <span className="truncate flex items-center gap-2"><MessageSquare size={13} />{s.title || 'Chat'}</span>
              )}
              <span className="flex items-center gap-1 shrink-0">
                {editingId === s.session_id ? (
                  <button onClick={(e) => saveRename(s.session_id, e)} data-testid="myra-rename-save" style={{ color: luxe.pink }}><Check size={14} /></button>
                ) : (
                  <>
                    <button onClick={(e) => startRename(s, e)} data-testid={`myra-rename-${s.session_id}`} className="opacity-0 group-hover:opacity-100" style={{ color: luxe.textMid }}><Pencil size={12} /></button>
                    <button onClick={(e) => delSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100" style={{ color: luxe.danger }}><Trash2 size={13} /></button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* chat */}
      <div className="flex-1 flex flex-col min-w-0 rounded-2xl" style={{ background: luxe.bgElev, border: `1px solid ${luxe.border}` }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: gradientCss(G), boxShadow: '0 12px 30px rgba(236,72,153,0.4)' }}><Sparkles size={30} color="#fff" /></div>
              <div className="font-display text-3xl font-black" style={{ color: luxe.text }}>Hi, I'm <span style={{ color: '#EC4899' }}>Myra</span></div>
              <div className="text-sm mt-2 mb-6" style={{ color: luxe.textMid }}>Kuch bhi poocho — sawaal, ideas, content, ya coding/prompts likhwao.</div>
              <div className="grid sm:grid-cols-2 gap-2 max-w-xl">
                {SUGGEST.map((s) => <button key={s} onClick={() => send(s)} className="text-left px-4 py-3 rounded-xl text-sm luxe-btn" style={{ background: luxe.surface, border: `1px solid ${luxe.border}`, color: luxe.textMid }}>{s}</button>)}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className="max-w-[85%] px-4 py-3 rounded-2xl text-sm myra-msg" style={{ background: m.role === 'user' ? gradientCss(['#4E9EFF', '#6366F1']) : luxe.surface, color: m.role === 'user' ? '#fff' : luxe.text, border: m.role === 'user' ? 'none' : `1px solid ${luxe.border}`, whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal', lineHeight: 1.6 }}>
                {m.role === 'assistant' && <div className="flex items-center gap-1.5 mb-1 text-[10px] font-extrabold uppercase tracking-widest" style={{ color: '#EC4899' }}><Sparkles size={11} /> Myra</div>}
                {m.role === 'assistant'
                  ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ''}</ReactMarkdown>
                  : m.content}
                {m.image_url && (
                  <div className="mt-2 relative group/img inline-block">
                    <img src={fileUrl(m.image_url)} alt="attachment" className="rounded-xl w-full max-w-sm" style={{ background: luxe.surface2 }} />
                    {m.role === 'assistant' && (
                      <a href={fileUrl(m.image_url)} download data-testid="chat-image-download" className="absolute top-2 right-2 w-8 h-8 rounded-lg flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }} title="Download"><Download size={15} /></a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="flex items-center gap-2 text-sm" style={{ color: luxe.muted }}><Loader2 size={16} className="animate-spin" style={{ color: '#EC4899' }} /> Myra soch rahi hai…</div>}
        </div>
        <div className="p-3" style={{ borderTop: `1px solid ${luxe.border}` }}>
          <div className="flex items-end gap-2 p-2 rounded-2xl" style={{ background: luxe.surface, border: `1px solid ${luxe.border}` }}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Message Myra…  (sawaal, content ya coding prompt)" data-testid="myra-chat-input" className="flex-1 outline-none resize-none bg-transparent text-sm py-2 px-2" style={{ color: luxe.text, maxHeight: 140 }} />
            <button onClick={() => send()} disabled={busy} data-testid="myra-chat-send" className="w-10 h-10 rounded-xl flex items-center justify-center luxe-btn shrink-0" style={{ background: gradientCss(G), opacity: busy ? 0.6 : 1 }}>{busy ? <Loader2 size={18} color="#fff" className="animate-spin" /> : <Send size={18} color="#fff" />}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
